import type { RendererFailure } from './renderer-failure';

export type ApplicationStartupStatus = 'loading' | 'ready' | 'failed' | 'unsupported';
export type StartupSceneStatus = 'initializing' | 'ready' | 'recovering' | 'failed' | 'unsupported';

/** Coordinates the first complete screen; subsequent data refreshes stay local. */
export const createApplicationStartup = () => {
  let enabled = false;
  let revealed = false;
  let mounted = false;
  let failed = false;
  let scene: StartupSceneStatus = 'initializing';
  let rendererFailure: RendererFailure | undefined;
  const pending = new Set<symbol>();
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach(listener => listener());
  const getStatus = (): ApplicationStartupStatus => {
    if (!enabled) return 'ready';
    if (failed || scene === 'failed') return 'failed';
    if (scene === 'unsupported') return 'unsupported';
    return mounted && scene === 'ready' && pending.size === 0 ? 'ready' : 'loading';
  };
  return {
    getStatus,
    getRendererFailure: () => rendererFailure,
    start: () => { enabled = true; emit(); },
    fail: () => { failed = true; emit(); },
    markMounted: () => { mounted = true; emit(); },
    markRevealed: () => { revealed = true; },
    /** Фиксирует сбой полной сцены для диагностики, не блокируя приложение. */
    reportRendererFailure: (failure: RendererFailure) => {
      rendererFailure = failure;
      emit();
    },
    setSceneStatus: (status: StartupSceneStatus, failure?: RendererFailure) => {
      scene = status;
      rendererFailure = failure;
      emit();
    },
    begin: () => {
      if (!enabled || revealed) return () => {};
      const token = Symbol('startup-task');
      pending.add(token);
      emit();
      return () => { if (pending.delete(token)) emit(); };
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  };
};

// Only index.web.js enables this coordinator. Native startup stays unchanged.
export const applicationStartup = createApplicationStartup();
